// =============================================================================
// SHANIZ GAMING ADMIN - LEADERBOARD PAGE
// =============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { leaderboardService } from '../services/api';
import type { GameType } from '../types';

// Game type options for dropdown
const gameTypes: { value: GameType; label: string }[] = [
  { value: 'LUDO', label: 'Ludo' },
  { value: 'CHESS', label: 'Chess' },
  { value: 'CHECKERS', label: 'Checkers' },
  { value: 'DRAFTS', label: 'Drafts' },
  { value: 'MANCALA', label: 'Mancala' },
  { value: 'FOUR_IN_A_ROW', label: 'Four in a Row' },
  { value: 'UNO', label: 'UNO' },
  { value: 'UNO_FLIP', label: 'UNO Flip' },
  { value: 'WORD_HUNT', label: 'Word Hunt' },
  { value: 'POOL', label: '8-Ball Pool' },
];

// Rank colors for top 3
const getRankColor = (rank: number): string => {
  switch (rank) {
    case 1: return '#FFD700'; // Gold
    case 2: return '#C0C0C0'; // Silver
    case 3: return '#CD7F32'; // Bronze
    default: return 'inherit';
  }
};

// Rank icon for top 3
const RankDisplay: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank <= 3) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrophyIcon sx={{ color: getRankColor(rank), fontSize: 24 }} />
        <Typography fontWeight={700} sx={{ color: getRankColor(rank) }}>
          #{rank}
        </Typography>
      </Box>
    );
  }
  return (
    <Typography color="text.secondary" fontWeight={500}>
      #{rank}
    </Typography>
  );
};

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedGame, setSelectedGame] = useState<GameType>('LUDO');

  // Fetch leaderboard data
  const { data: leaderboard = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['leaderboard', selectedGame],
    queryFn: () => leaderboardService.getGlobal(selectedGame, 100),
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Top-ranked players by ELO rating for the selected game. Click any row to open the player's profile.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Game Type</InputLabel>
            <Select
              value={selectedGame}
              label="Game Type"
              onChange={(e) => setSelectedGame(e.target.value as GameType)}
            >
              {gameTypes.map((game) => (
                <MenuItem key={game.value} value={game.value}>
                  {game.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon sx={{ animation: isFetching ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Leaderboard Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : !Array.isArray(leaderboard) || leaderboard.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <TrophyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No leaderboard data available
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Players will appear here once they start playing {gameTypes.find(g => g.value === selectedGame)?.label}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>Rank</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell align="center">ELO Rating</TableCell>
                    <TableCell align="center">Wins</TableCell>
                    <TableCell align="center">Losses</TableCell>
                    <TableCell align="center">Win Rate</TableCell>
                    <TableCell align="right">Total Earnings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaderboard.map((entry: any, index: number) => {
                    const rank = Number(entry?.rank) || index + 1;
                    const wins = Number(entry?.wins) || 0;
                    const losses = Number(entry?.losses) || 0;
                    // Backend sometimes sends winRate as a string like "57.5" — coerce.
                    const backendWinRate = entry?.winRate != null ? Number(entry.winRate) : undefined;
                    const winRate = (backendWinRate !== undefined && !Number.isNaN(backendWinRate))
                      ? backendWinRate.toFixed(1)
                      : (wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : '0.0');

                    return (
                      <TableRow
                        key={entry.userId || index}
                        hover
                        onClick={() => entry.userId && navigate(`/users/${entry.userId}`)}
                        sx={{
                          cursor: entry.userId ? 'pointer' : 'default',
                          bgcolor: rank <= 3 ? `${getRankColor(rank)}10` : 'inherit',
                          '&:hover': { bgcolor: rank <= 3 ? `${getRankColor(rank)}20` : 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <RankDisplay rank={rank} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={entry.profilePicture || entry.avatarUrl}
                              imgProps={{ loading: 'lazy', decoding: 'async' }}
                              sx={{
                                width: 40,
                                height: 40,
                                border: rank <= 3 ? `2px solid ${getRankColor(rank)}` : 'none',
                              }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography fontWeight={rank <= 3 ? 700 : 500}>
                                {entry.username || entry.user?.username || 'Unknown'}
                              </Typography>
                              {rank === 1 && (
                                <Chip
                                  label="Champion"
                                  size="small"
                                  sx={{
                                    bgcolor: '#FFD70030',
                                    color: '#FFD700',
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={entry.elo || entry.rating || 1200}
                            color={rank <= 3 ? 'primary' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography color="success.main" fontWeight={500}>
                            {wins}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography color="error.main" fontWeight={500}>
                            {losses}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${winRate}%`}
                            size="small"
                            sx={{
                              bgcolor: parseFloat(winRate) >= 50 ? 'success.900' : 'error.900',
                              color: parseFloat(winRate) >= 50 ? 'success.main' : 'error.main',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                            <Typography fontWeight={600} color={rank <= 3 ? 'success.main' : 'text.primary'}>
                              KES {(entry.totalEarnings || entry.earnings || 0).toLocaleString()}
                            </Typography>
                            <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {Array.isArray(leaderboard) && leaderboard.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Players
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {leaderboard.length}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Highest ELO
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary">
                {Math.max(...leaderboard.map((e: any) => Number(e?.elo ?? e?.rating ?? 1200) || 1200))}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Games Played
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {leaderboard.reduce((sum: number, e: any) => sum + (Number(e?.wins) || 0) + (Number(e?.losses) || 0), 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Earnings
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                KES {leaderboard.reduce((sum: number, e: any) => sum + (Number(e?.totalEarnings ?? e?.earnings) || 0), 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* CSS for spinning animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Leaderboard;
